import { formattedDollarsToCents, getErrorMessage } from '@fruitful/fruitils';
import { type BillCategory, type BillingCadence } from '@fruitful/types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    ArrowUpRightIcon,
    DollarInput,
    IconButton,
    Input,
    Spinner,
    SpinnerSize,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import type { UpsertBillBody } from '../../bills/useUpsertBill';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import type { Bill } from '../../entities/entityTypes';
import { captureException } from '../../logger/Logger';
import {
    billToMoneyMapperTableDraft,
    isMoneyMapperBillTableDraftUnchanged,
    type MoneyMapperBillTableDraft,
} from '../utils/moneyMapperBillTableDraft';
import {
    GroveMoneyMapPickerField,
    type MoneyMapPickerOption,
} from './GroveMoneyMapPickerField';
import {
    isFocusMovingWithinMoneyMapTableRow,
    type MoneyMapTableRowBlurEvent,
    useMoneyMapTableRowFocusWithin,
} from './moneyMapperTableRowFocus';
import {
    INCOME_TABLE_ACTIONS_OUTER_WIDTH,
    INCOME_TABLE_ROW_PADDING_HORIZONTAL,
    incomeTableAmountColumnProps,
    incomeTableBillCategoryColumnProps,
    incomeTablePayCycleColumnProps,
    incomeTableRowFocusActive,
    incomeTableRowFocusIdle,
    incomeTableSourceColumnProps,
} from './take-home-pay/moneyMapperIncomeTableLayout';

export interface MoneyMapperBillsTableRowProps {
    bill: Bill;
    canMutate: boolean;
    showCategoryPicker: boolean;
    categoryPickerOptions: Array<MoneyMapPickerOption<BillCategory>>;
    cadencePickerOptions: Array<MoneyMapPickerOption<BillingCadence>>;
    upsertBillAsync: (body: UpsertBillBody) => Promise<Bill>;
    onOpenDetails: () => void;
}

export const MoneyMapperBillsTableRow = ({
    bill,
    canMutate,
    showCategoryPicker,
    categoryPickerOptions,
    cadencePickerOptions,
    upsertBillAsync,
    onOpenDetails,
}: MoneyMapperBillsTableRowProps) => {
    const toast = useToastController();
    const [draft, setDraft] = useState<MoneyMapperBillTableDraft>(() =>
        billToMoneyMapperTableDraft(bill),
    );
    const [showNameInvalid, setShowNameInvalid] = useState(false);
    const [showAmountInvalid, setShowAmountInvalid] = useState(false);
    const [isRowSaving, setIsRowSaving] = useState(false);
    const draftRef = useRef(draft);
    draftRef.current = draft;
    const tableRowRef = useRef<Element | null>(null);
    const [tableRowRootElement, setTableRowRootElement] =
        useState<Element | null>(null);

    const assignTableRowRootRef = useCallback((node: unknown) => {
        const rootElement = node instanceof Element ? node : null;
        tableRowRef.current = rootElement;
        setTableRowRootElement(rootElement);
    }, []);

    const hasFocusWithinTableRow =
        useMoneyMapTableRowFocusWithin(tableRowRootElement);

    useEffect(() => {
        setDraft(billToMoneyMapperTableDraft(bill));
        setShowNameInvalid(false);
        setShowAmountInvalid(false);
    }, [
        bill.id,
        bill.amount,
        bill.cadence,
        bill.category,
        bill.displayName,
        bill.name,
        bill.payee,
    ]);

    const disabled = !canMutate || isRowSaving;

    const tryPersist = async (next: MoneyMapperBillTableDraft) => {
        if (!canMutate || isRowSaving) {
            return;
        }
        const name = next.displayName.trim();
        if (name === '') {
            return;
        }
        const amountCents = formattedDollarsToCents(next.amountStr);
        if (!Number.isFinite(amountCents) || amountCents <= 0) {
            return;
        }
        if (isMoneyMapperBillTableDraftUnchanged(bill, next)) {
            return;
        }

        setIsRowSaving(true);
        try {
            await upsertBillAsync({
                id: bill.id,
                membershipId: bill.membershipId,
                displayName: name,
                payee: next.payee.trim() || name,
                amount: amountCents,
                accountId: bill.accountId,
                category: next.category,
                cadence: next.cadence,
                activationStatus: bill.activationStatus,
            });
            toast.show('Bill saved', MutationSuccessToastOptions);
        } catch (error) {
            captureException(error);
            toast.show(
                'Could not save bill',
                MutationErrorToastOptions(
                    getErrorMessage(error, 'Something went wrong. Try again.'),
                ),
            );
        } finally {
            setIsRowSaving(false);
        }
    };

    const persistIfLeavingRow = (event: MoneyMapTableRowBlurEvent) => {
        const root = tableRowRef.current;
        if (isFocusMovingWithinMoneyMapTableRow(root, event)) {
            return;
        }
        void tryPersist(draftRef.current);
    };

    return (
        <XStack
            ref={assignTableRowRootRef}
            width="100%"
            alignItems="flex-start"
            paddingVertical={12}
            paddingHorizontal={INCOME_TABLE_ROW_PADDING_HORIZONTAL}
            gap={8}
            {...(hasFocusWithinTableRow
                ? incomeTableRowFocusActive
                : incomeTableRowFocusIdle)}
        >
            <YStack {...incomeTableSourceColumnProps}>
                <Input
                    size="regular"
                    value={draft.displayName}
                    invalid={showNameInvalid}
                    onChangeText={(t) => {
                        setDraft((d) => ({ ...d, displayName: t }));
                        if (t.trim().length > 0) {
                            setShowNameInvalid(false);
                        }
                    }}
                    onBlur={(e) => {
                        setShowNameInvalid(
                            draftRef.current.displayName.trim() === '',
                        );
                        if (
                            !isFocusMovingWithinMoneyMapTableRow(
                                tableRowRef.current,
                                e,
                            )
                        ) {
                            void tryPersist(draftRef.current);
                        }
                    }}
                    placeholder="Bill name"
                    disabled={disabled}
                />
            </YStack>
            {showCategoryPicker ? (
                <YStack {...incomeTableBillCategoryColumnProps}>
                    <GroveMoneyMapPickerField
                        size="regular"
                        value={draft.category}
                        options={categoryPickerOptions}
                        disabled={disabled}
                        onTriggerBlur={persistIfLeavingRow}
                        onChange={(v) => {
                            const next = { ...draft, category: v };
                            setDraft(next);
                            void tryPersist(next);
                        }}
                    />
                </YStack>
            ) : null}
            <YStack {...incomeTableAmountColumnProps}>
                <DollarInput
                    size="regular"
                    value={draft.amountStr}
                    invalid={showAmountInvalid}
                    onChangeText={(t) => {
                        setDraft((d) => ({ ...d, amountStr: t }));
                        const cents = formattedDollarsToCents(t);
                        if (Number.isFinite(cents) && cents > 0) {
                            setShowAmountInvalid(false);
                        }
                    }}
                    onBlur={(e) => {
                        const cents = formattedDollarsToCents(
                            draftRef.current.amountStr,
                        );
                        setShowAmountInvalid(
                            !Number.isFinite(cents) || cents <= 0,
                        );
                        if (
                            !isFocusMovingWithinMoneyMapTableRow(
                                tableRowRef.current,
                                e,
                            )
                        ) {
                            void tryPersist(draftRef.current);
                        }
                    }}
                    allowDecimals
                    disabled={disabled}
                />
            </YStack>
            <YStack {...incomeTablePayCycleColumnProps}>
                <GroveMoneyMapPickerField
                    size="regular"
                    value={draft.cadence}
                    options={cadencePickerOptions}
                    disabled={disabled}
                    onTriggerBlur={persistIfLeavingRow}
                    onChange={(v) => {
                        const next = { ...draft, cadence: v };
                        setDraft(next);
                        void tryPersist(next);
                    }}
                />
            </YStack>
            <XStack
                width={INCOME_TABLE_ACTIONS_OUTER_WIDTH}
                flexShrink={0}
                minHeight={44}
                alignItems="flex-start"
                justifyContent="flex-end"
                paddingTop={4}
                gap={4}
            >
                {isRowSaving ? (
                    <YStack
                        width={28}
                        minHeight={32}
                        alignItems="center"
                        justifyContent="center"
                        paddingTop={2}
                    >
                        <Spinner size={SpinnerSize.SMALL} />
                    </YStack>
                ) : (
                    <IconButton
                        variant="tertiary"
                        icon={ArrowUpRightIcon}
                        disabled={disabled}
                        onPress={onOpenDetails}
                    />
                )}
            </XStack>
        </XStack>
    );
};
